import React, { useState } from 'react';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import DeleteModal from '../../../components/DeleteModal';
import { Pagination } from '../../../components/Pagination';
import { ClientError } from '../../../services/central-api/base-service';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { DocumentsParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import DropdownButton from '../../../components/DropdownButton';
import { documentService } from '../../../services/central-api/document';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import DocumentCategoryModal from './DocumentCategoryModal';
import Filter from './Filters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';

export default function DocumentCategories() {
    const route = useRoute<RouteProp<DocumentsParamList, 'DocumentCategories'>>();
    const navigation = useNavigation<NavigationProp<DocumentsParamList>>();
    const [filters, setFilters] = useFilters<DocumentsParamList['DocumentCategories']>({
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedDocumentCategory, setSelectedDocumentCategory] = useState<DocumentCategory | undefined>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getDocumentCategories', filters],
        queryFn: async () => documentService.getDocumentCategories(filters),
        enabled: isFocused,
    });

    async function confirmDelete(category: DocumentCategory) {
        try {
            await documentService.deleteDocumentCategory(category.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            handleModalClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFilters() {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    function handleModalClose() {
        setSelectedDocumentCategory(undefined);
        setModalVisible(false);
        refetch();
    }

    return (
        <Container>
            <Header
                title={translate('documentCategories')}
                actions={<CustomButton text={translate('register')} onPress={() => {
                    setSelectedDocumentCategory(undefined);
                    setModalVisible(true);
                }} />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredDocumentCategories')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('type'),
                                selector: row => row.type,
                                wrap: true,
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBodyUnit.actingBody.name,
                                wrap: true,
                            },
                            {
                                name: translate('unit'),
                                selector: row => row.actingBodyUnit.name,
                                wrap: true,
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                                wrap: true,
                            },
                            {
                                cell: row => (
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => {
                                                    setSelectedDocumentCategory(row);
                                                    setModalVisible(true);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    setSelectedDocumentCategory(row);
                                                    setDeleteModalVisible(true);
                                                },
                                            }
                                        ]}
                                    />
                                ),
                                wrap: true,
                                button: true,
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                    />}
                />
            </Card>

            <DocumentCategoryModal
                onClose={handleModalClose}
                isModalVisible={isModalVisible}
                documentCategoryId={selectedDocumentCategory?.id}
            />

            {selectedDocumentCategory && <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedDocumentCategory.type}
                onSubmit={async () => {
                    await confirmDelete(selectedDocumentCategory);
                }}
                onCancel={() => setSelectedDocumentCategory(undefined)}
            />}
        </Container>
    );
}
